import React, { useState, useEffect } from 'react';
import { Modal, Button, Card, Row, Col, Alert } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { canEditItem } from '../utils/permissionUtils';
import LineItemBudgetModal from './LineItemBudgetModal';
import ProjectProposalEditModal from './ProjectProposalEditModal';
import axios from 'axios';

const ProjectEditModal = ({ show, onHide, project }) => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showBudgetModal, setShowBudgetModal] = useState(false);
  const [showProposalEditModal, setShowProposalEditModal] = useState(false);
  const [projectData, setProjectData] = useState(null);

  // Check if user can edit this project using centralized permission system
  const canEdit = user && project && canEditItem(user, project, 'project').canEdit;

  // Helper function to format fund source for display
  const formatFundSource = (fundSource) => {
    if (!fundSource) return 'Not specified';
    
    // If it's a JSON string, parse it
    if (typeof fundSource === 'string') {
      try {
        const parsed = JSON.parse(fundSource);
        if (Array.isArray(parsed)) {
          return parsed.join(', ');
        }
        return parsed;
      } catch (e) {
        // Not JSON, return as-is
        return fundSource;
      }
    }
    
    // If it's already an array
    if (Array.isArray(fundSource)) {
      return fundSource.join(', ');
    }
    
    return String(fundSource);
  };

  // Check if project has STF/MDS fund source (enables LIB form editing)
  const hasSTFOrMDS = project && project.fund_source && (
    project.fund_source.includes('STF') || 
    project.fund_source.includes('MDS') ||
    project.fund_source.includes('Special Trust Fund') ||
    project.fund_source.includes('Miscellaneous and Other Sources')
  );

  // Handle edit project proposal
  const handleEditProposal = () => {
    if (!project) return;
    onHide();
    // Show the project proposal edit modal
    setShowProposalEditModal(true);
  };

  // Handle edit LIB form - show budget modal
  const handleEditLIB = async () => {
    if (!project) return;
    
    try {
      setLoading(true);
      // Fetch full project data
      const response = await axios.get(`/projects/${project.project_id}`);
      setProjectData(response.data);
      // Show budget modal - parent modal will hide automatically via conditional rendering
      setShowBudgetModal(true);
    } catch (error) {
      console.error('Error fetching project data:', error);
      setError('Failed to load project data');
    } finally {
      setLoading(false);
    }
  };

  if (!project) return null;

  return (
    <>
    <Modal 
      show={show && !showBudgetModal} 
      onHide={onHide} 
      size="lg" 
      centered
      style={{ zIndex: 10100 }}
      backdrop="static"
      keyboard={false}
      backdropClassName="modal-backdrop"
    >
      <Modal.Header closeButton style={{ position: 'relative', zIndex: 10120, backgroundColor: '#fff' }}>
        <Modal.Title className="d-flex align-items-center">
          <i className="bi bi-pencil-square me-2"></i>
          Edit Project: {project.title}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {!canEdit && (
          <Alert variant="warning" className="mb-4">
            <i className="bi bi-exclamation-triangle me-2"></i>
            You don't have permission to edit this project.
          </Alert>
        )}

        {canEdit && (
          <>
            <div className="text-center mb-4">
              <p className="text-muted">Choose what you would like to edit for this project:</p>
            </div>
            
            <Row className="g-3">
              {/* Project Proposal Edit */}
              <Col md={hasSTFOrMDS ? 6 : 12}>
                <Card className="h-100 border-0 shadow-sm">
                  <Card.Body className="text-center p-4">
                    <i className="bi bi-file-earmark-text-fill text-primary mb-3" style={{ fontSize: '2.5rem' }}></i>
                    <h5 className="card-title">Project Proposal</h5>
                    <p className="card-text text-muted small">
                      Edit project details, objectives, timeline, and other proposal information
                    </p>
                    <Button 
                      variant="primary" 
                      onClick={handleEditProposal}
                      className="w-100"
                    >
                      <i className="bi bi-pencil me-2"></i>
                      Edit Proposal
                    </Button>
                  </Card.Body>
                </Card>
              </Col>

              {/* LIB Form Edit - Only show if STF/MDS */}
              {hasSTFOrMDS && (
                <Col md={6}>
                  <Card className="h-100 border-0 shadow-sm">
                    <Card.Body className="text-center p-4">
                      <i className="bi bi-calculator-fill text-success mb-3" style={{ fontSize: '2.5rem' }}></i>
                      <h5 className="card-title">Line-Item Budget</h5>
                      <p className="card-text text-muted small">
                        Edit detailed budget breakdown and financial allocations
                      </p>
                      <Button 
                        variant="success" 
                        onClick={handleEditLIB}
                        className="w-100"
                      >
                        <i className="bi bi-calculator me-2"></i>
                        Edit Budget
                      </Button>
                    </Card.Body>
                  </Card>
                </Col>
              )}

              {/* Show message if no STF/MDS */}
              {!hasSTFOrMDS && (
                <Col md={12}>
                  <Alert variant="info" className="mb-0">
                    <i className="bi bi-info-circle me-2"></i>
                    <strong>Line-Item Budget editing is only available for STF/MDS funded projects.</strong>
                    <br />
                    <small>This project's fund source: <strong>{formatFundSource(project.fund_source)}</strong></small>
                  </Alert>
                </Col>
              )}
            </Row>

            {/* Project Info Summary */}
            <div className="mt-4 p-3 bg-light rounded">
              <h6 className="mb-2">
                <i className="bi bi-info-circle me-2"></i>
                Project Information
              </h6>
              <Row>
                <Col md={6}>
                  <small className="text-muted">
                    <strong>Status:</strong> {project.status}
                  </small>
                </Col>
                <Col md={6}>
                  <small className="text-muted">
                    <strong>Fund Source:</strong> {formatFundSource(project.fund_source)}
                  </small>
                </Col>
                <Col md={6}>
                  <small className="text-muted">
                    <strong>Submitted:</strong> {new Date(project.date_submitted).toLocaleDateString()}
                  </small>
                </Col>
                <Col md={6}>
                  <small className="text-muted">
                    <strong>Last Updated:</strong> {new Date(project.last_updated).toLocaleDateString()}
                  </small>
                </Col>
              </Row>
            </div>
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="outline-secondary" onClick={onHide}>
          <i className="bi bi-x-lg me-1"></i>
          Cancel
        </Button>
      </Modal.Footer>
    </Modal>

      {/* Line-Item Budget Modal - Render outside parent modal to avoid z-index issues */}
      {showBudgetModal && (
        <LineItemBudgetModal
          show={showBudgetModal}
          onHide={() => {
            setShowBudgetModal(false);
            setProjectData(null);
          }}
          projectId={project?.project_id}
          projectData={projectData}
          onSuccess={() => {
            setProjectData(null);
          }}
        />
      )}

      {/* Project Proposal Edit Modal */}
      {showProposalEditModal && (
        <ProjectProposalEditModal
          show={showProposalEditModal}
          onHide={() => {
            setShowProposalEditModal(false);
          }}
          projectId={project?.project_id}
          onSuccess={() => {
            setShowProposalEditModal(false);
            // Optionally refresh the project data or trigger a parent refresh
            if (onHide) onHide();
          }}
        />
      )}
    </>
  );
};

export default ProjectEditModal;
